import {
  GridHelper,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  ShaderMaterial,
  Color,
} from 'three';

let ground = null;

export function initGround(scene, world, options = {}) {
  const {
    segments = 64,
    gridDivisions = 40,
  } = options;

  // Base floor — dark reflective surface (acts as mirror for bloom glow)
  const floorGeometry = new PlaneGeometry(world.size, world.size, segments, segments);
  const floorMaterial = new MeshStandardMaterial({
    color: 0x07101f,
    roughness: 0.38,
    metalness: 0.72,
    envMapIntensity: 1.2,
  });

  const floor = new Mesh(floorGeometry, floorMaterial);
  floor.name = 'floor';
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  floor.userData.surface = 'asphalt';
  scene.add(floor);

  // Primary neon grid — bright accent lines
  const grid = new GridHelper(world.size, gridDivisions, 0x53ffb5, 0x1d3d55);
  grid.name = 'grid';
  grid.position.y = 0.018;
  grid.material.transparent = true;
  grid.material.opacity = 0.55;
  grid.material.depthWrite = false;
  scene.add(grid);

  // Secondary sub-grid — finer detail with purple accent
  const subGrid = new GridHelper(world.size, gridDivisions * 4, 0x6a5aff, 0x6a5aff);
  subGrid.name = 'subgrid';
  subGrid.position.y = 0.012;
  subGrid.material.transparent = true;
  subGrid.material.opacity = 0.08;
  subGrid.material.depthWrite = false;
  scene.add(subGrid);

  // Emissive glow plane underneath — adds luminosity to floor near action
  const glowGeometry = new PlaneGeometry(world.size * 0.6, world.size * 0.6);
  const glowMaterial = new MeshStandardMaterial({
    color: 0x53ffb5,
    emissive: new Color(0x53ffb5),
    emissiveIntensity: 0.06,
    roughness: 1.0,
    metalness: 0.0,
    transparent: true,
    opacity: 0.0,
  });
  const glowPlane = new Mesh(glowGeometry, glowMaterial);
  glowPlane.name = 'floorGlow';
  glowPlane.rotation.x = -Math.PI / 2;
  glowPlane.position.y = 0.005;
  scene.add(glowPlane);

  world.floor = floor;
  world.grid = grid;
  world.subGrid = subGrid;
  world.glowPlane = glowPlane;
  world.baseSize = world.size;

  ground = { floor, grid, subGrid, glowPlane, floorGeometry, floorMaterial };
  return ground;
}

export function getGround() {
  if (!ground) {
    throw new Error('[ground] initGround() must run before getGround().');
  }
  return ground;
}

export function scaleGround(world) {
  if (!ground || !world.baseSize) return;

  const scale = world.size / world.baseSize;
  ground.floor.scale.setScalar(scale);
  ground.grid.scale.setScalar(scale);
  ground.subGrid.scale.setScalar(scale);
}

export function setGridOpacity(opacity) {
  if (!ground?.grid?.material) return;
  ground.grid.material.opacity = Math.min(0.72, opacity);
  if (ground.subGrid?.material) ground.subGrid.material.opacity = opacity * 0.15;
}

/** Pulse the floor glow at the player position — call every frame */
export function pulseFloorGlow(playerPos, elapsed) {
  if (!ground?.glowPlane) return;
  const pulse = 0.04 + Math.sin(elapsed * 1.8) * 0.02;
  ground.glowPlane.material.emissiveIntensity = pulse;
  if (playerPos) {
    ground.glowPlane.position.x = playerPos.x * 0.15;
    ground.glowPlane.position.z = playerPos.z * 0.15;
  }
}
